import { type ApolloError } from "@apollo/client";
import { useCallback, useRef, useState } from "react";

import {
  type PieceBackfillRunFieldsFragment,
  type StartPieceBackfillRunInput,
  usePieceBackfillRunQuery,
  useStartPieceBackfillRunMutation,
} from "../../generated/graphql";
import {
  isTerminalRunStatus,
  POLL_INTERVAL_MS,
  shouldStopPollingAfterFailures,
} from "./piece-backfill-run-state";

/** Options for {@link usePieceBackfillRun}. */
export type UsePieceBackfillRunOptions = {
  /**
   * Called once, with the final run, when a run reaches a terminal state. Used
   * to refresh run history and to unlock EXECUTE after a dry run completes.
   */
  readonly onRunFinished?: (run: PieceBackfillRunFieldsFragment) => void;
};

/** Return shape of {@link usePieceBackfillRun}. */
export type UsePieceBackfillRun = {
  /** Starts a run and begins polling it. */
  readonly start: (input: StartPieceBackfillRunInput) => Promise<void>;
  /** True while the start mutation is in flight. */
  readonly starting: boolean;
  /** Error from the start mutation, if any. */
  readonly startError?: ApolloError;
  /** The latest state of the active run, or null before the first run. */
  readonly run: PieceBackfillRunFieldsFragment | null;
  /** Error from the most recent poll, if the poll is currently failing. */
  readonly pollError?: ApolloError;
  /** True once polling gave up after repeated failures. */
  readonly pollAbandoned: boolean;
};

/**
 * Orchestrates one piece-backfill run from the UI: starts it, then polls it
 * until it reaches COMPLETED or FAILED, at which point polling stops and
 * `onRunFinished` fires exactly once for that run.
 *
 * Polling tolerates a few consecutive read failures before giving up, so a brief
 * network blip does not abandon a run that is still progressing server-side.
 * Giving up only stops the client from reading: the run itself continues, and
 * the history panel will show its outcome.
 *
 * A start failure is surfaced via `startError` and never rejects the returned
 * promise, so a fire-and-forget caller cannot produce an unhandled rejection.
 *
 * @param options Terminal-state callback.
 * @returns The start action plus the active run's live state.
 */
export const usePieceBackfillRun = (
  options: UsePieceBackfillRunOptions = {},
): UsePieceBackfillRun => {
  const { onRunFinished } = options;
  const [runUuid, setRunUuid] = useState<string | null>(null);
  const [pollAbandoned, setPollAbandoned] = useState(false);
  const consecutiveFailuresRef = useRef(0);
  const announcedRunUuidRef = useRef<string | null>(null);

  const [startMutation, { loading: starting, error: startError }] =
    useStartPieceBackfillRunMutation();

  const {
    data,
    error: pollError,
    startPolling,
    stopPolling,
  } = usePieceBackfillRunQuery({
    variables: { uuid: runUuid ?? "" },
    skip: runUuid === null,
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: (result) => {
      consecutiveFailuresRef.current = 0;
      const polled = result.pieceBackfillRun;
      if (!isTerminalRunStatus(polled.status)) {
        return;
      }
      stopPolling();
      if (announcedRunUuidRef.current === polled.uuid) {
        return;
      }
      announcedRunUuidRef.current = polled.uuid;
      onRunFinished?.(polled);
    },
    onError: () => {
      consecutiveFailuresRef.current += 1;
      if (shouldStopPollingAfterFailures(consecutiveFailuresRef.current)) {
        stopPolling();
        setPollAbandoned(true);
      }
    },
  });

  const start = useCallback(
    async (input: StartPieceBackfillRunInput) => {
      try {
        consecutiveFailuresRef.current = 0;
        announcedRunUuidRef.current = null;
        setPollAbandoned(false);
        setRunUuid(null);
        const result = await startMutation({ variables: { input } });
        const startedUuid = result.data?.startPieceBackfillRun.uuid ?? null;
        setRunUuid(startedUuid);
        if (startedUuid !== null) {
          startPolling(POLL_INTERVAL_MS);
        }
      } catch {
        // The mutation error is surfaced via `startError`; swallow it here so a
        // fire-and-forget caller does not see an unhandled rejection.
      }
    },
    [startMutation, startPolling],
  );

  return {
    start,
    starting,
    startError,
    run: data?.pieceBackfillRun ?? null,
    pollError,
    pollAbandoned,
  };
};
